"use client";

import { c as _c } from "react/compiler-runtime";
import { Mic, MicOff, Volume2 } from "lucide-react";
import { cn } from "@/lib/utils";
interface VoiceActivityIndicatorProps {
  /** The call is live; before that there is nothing to meter. */
  live: boolean;
  muted: boolean;
  userSpeaking: boolean;
  assistantSpeaking: boolean;
  inputLevel: number;
  outputLevel: number;
  className?: string;
}

/** Centre bars react most, so the meter reads as a waveform rather than a bar chart. */
const BAR_WEIGHTS = [0.45, 0.75, 1, 0.75, 0.45];
const METER_HEIGHT_PX = 16;
const METER_MIN_FRACTION = 0.18;
function barHeight(level: number, weight: number): number {
  const scaled = Math.min(1, Math.max(0, level) * weight);
  const variable = (1 - METER_MIN_FRACTION) * scaled;
  return Math.round((METER_MIN_FRACTION + variable) * METER_HEIGHT_PX);
}
function LevelMeter(t0) {
  const $ = _c(5);
  const {
    level,
    className
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== level) {
    t1 = BAR_WEIGHTS.map((weight, index) => <span key={index} className={cn("w-[3px] rounded-full transition-[height] duration-100 ease-out", className)} style={{
      height: `${barHeight(level, weight)}px`
    }} />);
    $[0] = className;
    $[1] = level;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <span className="flex h-4 items-center gap-[3px]" aria-hidden="true">{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/**
 * Live "who has the floor" readout for a realtime call: the microphone meter
 * proves audio is reaching us, and the label reflects the server's own VAD
 * decision about whether the user's turn is being captured.
 */
export function VoiceActivityIndicator(t0) {
  const $ = _c(18);
  const {
    live,
    muted,
    userSpeaking,
    assistantSpeaking,
    inputLevel,
    outputLevel,
    className
  } = t0;
  if (!live) {
    return null;
  }
  const state = muted ? "muted" : assistantSpeaking ? "assistant" : userSpeaking ? "listening" : "idle";
  const label = state === "muted" ? "Microphone muted" : state === "assistant" ? "Assistant speaking" : state === "listening" ? "Listening\u2026" : "Waiting for you to speak";
  const t1 = state === "muted" && "text-muted-foreground border-dashed";
  const t2 = state === "assistant" && "border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400";
  const t3 = state === "listening" && "border-green-500/40 bg-green-500/10 text-green-600 dark:text-green-400";
  const t4 = state === "idle" && "text-muted-foreground";
  let t5;
  if ($[0] !== className || $[1] !== t1 || $[2] !== t2 || $[3] !== t3 || $[4] !== t4) {
    t5 = cn("flex items-center gap-2.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors", t1, t2, t3, t4, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== state) {
    t6 = state === "muted" ? <MicOff className="h-3.5 w-3.5" /> : state === "assistant" ? <Volume2 className="h-3.5 w-3.5" /> : <Mic className={cn("h-3.5 w-3.5", state === "listening" && "animate-pulse")} />;
    $[6] = state;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== label) {
    t7 = <span>{label}</span>;
    $[8] = label;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = state === "muted" ? 0 : state === "assistant" ? outputLevel : inputLevel;
  const t9 = state === "assistant" ? "bg-blue-500" : state === "listening" ? "bg-green-500" : "bg-muted-foreground/40";
  let t10;
  if ($[10] !== t8 || $[11] !== t9) {
    t10 = <LevelMeter level={t8} className={t9} />;
    $[10] = t8;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t11 = <div role="status" aria-live="polite" className={t5}>{t6}{t7}{t10}</div>;
    $[13] = t10;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
}